import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MapPinIcon, ClockIcon, ShieldCheckIcon, CheckCircleIcon, ArrowRightIcon, AlertCircleIcon, MessageSquareIcon } from 'lucide-react';
const SMS_LINK = 'sms:8624072386?body=Hi,%20I%20need%20a%20free%20gutter%20estimate.%20My%20name%20is%20___%20and%20my%20address%20is%20___.';
export function ContactForm() {
  return <section id="contact" className="relative py-24 bg-gray-50 overflow-hidden" data-id="element-44">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 20% 50%, rgba(11,122,196,0.06) 0%, transparent 50%), radial-gradient(ellipse at 80% 50%, rgba(122,201,67,0.04) 0%, transparent 50%)'
    }} data-id="element-45" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-46">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-47">
          <p className="text-[#0B7AC4] font-bold uppercase tracking-widest text-sm mb-3" data-id="element-48">
            Get Started
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-4" data-id="element-49">
            Get Your Free Estimate
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#0B7AC4] to-[#7AC943] mx-auto rounded-full mb-6" data-id="element-50" />
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light" data-id="element-51">
            Tap the button below, call, or text us directly. We respond to all
            inquiries within 24 hours.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-2xl overflow-hidden shadow-2xl border border-gray-100" data-id="element-52">
          {/* CTA Panel */}
          <div className="lg:col-span-3 bg-white p-8 md:p-12 flex flex-col items-center justify-center text-center" data-id="element-53">
            <motion.div initial={{
            opacity: 0,
            scale: 0.92
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="w-full max-w-md" data-id="element-54">
              <div className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-8" style={{
              background: 'rgba(11,122,196,0.1)',
              border: '2px solid rgba(11,122,196,0.3)'
            }} data-id="element-55">
                <MessageSquareIcon className="w-10 h-10 text-[#0B7AC4]" data-id="element-56" />
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4 uppercase tracking-wide" data-id="element-57">
                Ready for Your Free Estimate?
              </h3>
              <p className="text-gray-500 mb-8 leading-relaxed" data-id="element-58">
                Tap the button below to text us directly. We'll respond with
                your personalized estimate within 24 hours.
              </p>

              {/* Primary CTA — SMS link styled as the original submit button */}
              <a href={SMS_LINK} className="btn-primary btn-ripple w-full text-lg flex items-center justify-center gap-2" data-id="element-59">
                Request Free Estimate <ArrowRightIcon className="w-5 h-5" data-id="element-60" />
              </a>

              {/* Call button */}
              <a href="tel:8624072386" className="btn-yellow btn-ripple w-full text-lg flex items-center justify-center gap-2 mt-4" data-id="element-61">
                <PhoneIcon className="w-5 h-5" data-id="element-62" />
                Call (862) 407-2386
              </a>

              <p className="text-center text-xs text-gray-400 mt-6" data-id="element-63">
                🔒 Your information is secure. We never share your data.
              </p>
            </motion.div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 p-8 md:p-12 relative overflow-hidden" style={{
          background: 'linear-gradient(160deg, #0A1628 0%, #0d1f3c 100%)'
        }} data-id="element-64">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(11,122,196,0.2) 0%, transparent 70%)'
          }} data-id="element-65" />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(122,201,67,0.15) 0%, transparent 70%)'
          }} data-id="element-66" />

            <div className="relative z-10" data-id="element-67">
              <h3 className="text-2xl font-black text-white mb-2" data-id="element-68">
                Contact Us Directly
              </h3>
              <p className="text-gray-400 text-sm mb-10" data-id="element-69">
                We&apos;re available 7 days a week.
              </p>

              <div className="space-y-8" data-id="element-70">
                <a href="tel:8624072386" className="flex items-start gap-4 group" data-id="element-71">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110" style={{
                  background: 'rgba(255,212,0,0.15)',
                  border: '1px solid rgba(255,212,0,0.4)',
                  boxShadow: '0 0 20px rgba(255,212,0,0.15)'
                }} data-id="element-72">
                    <PhoneIcon className="w-5 h-5 text-[#FFD400]" data-id="element-73" />
                  </div>
                  <div data-id="element-74">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-75">
                      Call Us Directly
                    </p>
                    <p className="text-2xl font-bold text-white group-hover:text-[#FFD400] transition-colors uppercase tracking-wide" data-id="element-76">
                      (862) 407-2386
                    </p>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-77">
                      Alfonseli Then (Owner)
                    </p>
                  </div>
                </a>

                {/* SMS Direct */}
                <a href={SMS_LINK} className="flex items-start gap-4 group" data-id="element-78">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110" style={{
                  background: 'rgba(122,201,67,0.15)',
                  border: '1px solid rgba(122,201,67,0.4)',
                  boxShadow: '0 0 20px rgba(122,201,67,0.15)'
                }} data-id="element-79">
                    <MessageSquareIcon className="w-5 h-5 text-[#7AC943]" data-id="element-80" />
                  </div>
                  <div data-id="element-81">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-82">
                      Text Us Now
                    </p>
                    <p className="text-lg font-bold text-white group-hover:text-[#7AC943] transition-colors uppercase tracking-wide" data-id="element-83">
                      Tap to Send a Text
                    </p>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-84">
                      Opens your messaging app
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-id="element-85">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                  background: 'rgba(11,122,196,0.15)',
                  border: '1px solid rgba(11,122,196,0.3)'
                }} data-id="element-86">
                    <MapPinIcon className="w-5 h-5 text-[#0B7AC4]" data-id="element-87" />
                  </div>
                  <div data-id="element-88">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-89">
                      Headquarters
                    </p>
                    <p className="text-white font-semibold leading-relaxed" data-id="element-90">
                      988 E 19th St 2nd Floor
                      <br data-id="element-91" />
                      Paterson, NJ 07501
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-92">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                  background: 'rgba(249,115,22,0.15)',
                  border: '1px solid rgba(249,115,22,0.3)'
                }} data-id="element-93">
                    <ClockIcon className="w-5 h-5 text-[#F97316]" data-id="element-94" />
                  </div>
                  <div data-id="element-95">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-96">
                      Business Hours
                    </p>
                    <p className="text-white font-semibold" data-id="element-97">
                      Mon–Sat: 7:00 AM – 7:00 PM
                    </p>
                    <p className="text-white font-semibold" data-id="element-98">
                      Sun: 8:00 AM – 5:00 PM
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8" style={{
              borderTop: '1px solid rgba(255,255,255,0.08)'
            }} data-id="element-99">
                <div className="flex items-center gap-3 p-4 rounded-2xl" style={{
                background: 'rgba(122,201,67,0.1)',
                border: '1px solid rgba(122,201,67,0.25)'
              }} data-id="element-100">
                  <ShieldCheckIcon className="w-7 h-7 text-[#7AC943] flex-shrink-0" data-id="element-101" />
                  <div data-id="element-102">
                    <p className="font-black text-white text-sm" data-id="element-103">
                      Licensed & Insured
                    </p>
                    <p className="text-gray-400 text-xs" data-id="element-104">
                      NJHICRA #13VH08498200
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}